/**
 * <nosto-react> — bridge between the Liquid skeleton and the Preact app.
 *
 * Watches `#nosto-content` (where `Category`, `Serp`, etc. mount via portal)
 * for child insertions. Once the first child appears, marks the wrapper
 * `.nosto-loaded` so CSS hides the skeleton and reveals the React tree.
 *
 * If no child shows up within `TIMEOUT_MS`, marks the wrapper `.nosto-fallback`
 * so CSS reveals the native Liquid fallback (the original Dawn collection
 * grid). This is the safety net the user asked for: if Nosto fails or is
 * slow, the customer never sees an empty page.
 *
 * Dispatched events:
 *   - `nosto:ready`  → bubbles when content mounts
 *   - `nosto:fallback` → bubbles when timeout fires
 */

// Délai avant de basculer sur le filet de sécurité natif si Nosto ne rend pas.
// Volontairement élevé : on préfère garder le skeleton (calibré, iso) plus
// longtemps plutôt que d'afficher brièvement la grille native PUIS Nosto
// (double affichage natif→Nosto que le client a signalé). Le natif ne doit
// apparaître qu'en cas d'échec réel de Nosto.
const TIMEOUT_MS = 12000;

class NostoReact extends HTMLElement {
	connectedCallback() {
		this._timeoutId = setTimeout(() => this._fallback(), TIMEOUT_MS);

		const target = this.querySelector('#nosto-content');
		if (!target) {
			this._fallback();
			return;
		}

		// Already mounted by the time we connect (rare but happens with cache hits)
		if (this._isContentReady(target)) {
			this._scheduleReady();
			return;
		}

		this._observer = new MutationObserver(() => {
			if (this._isContentReady(target)) this._scheduleReady();
		});
		this._observer.observe(target, { childList: true, subtree: true });
	}

	/**
	 * Anti-CLS crucial : ne PAS reveler #nosto-content au premier enfant insere.
	 *
	 * Nosto (Preact) monte d'abord le conteneur + la barre d'outils (qq dizaines
	 * de px) AVANT de peupler la grille produit. Si on revele a cet instant, on
	 * masque le skeleton de section alors que #nosto-content est quasi vide : la
	 * page se replie (le contenu sous la grille remonte de ~1800px) puis se
	 * redeploie quand la grille arrive => CLS massif.
	 *
	 * On attend donc que la grille produit soit reellement peuplee (>=1 carte),
	 * OU qu'un etat « aucun resultat » soit rendu (sinon on resterait bloque sur
	 * une recherche vide jusqu'au timeout fallback).
	 */
	_isContentReady(target) {
		const grid = target.querySelector('ul.product-grid, ul.grid');
		if (grid && grid.children.length > 0) return true;
		// Etat sans resultat (NoResults) : pas de grille mais du contenu reel.
		if (target.querySelector('[data-nosto-no-results], .no-results, .nosto-no-results')) return true;
		return false;
	}

	/**
	 * Une fois la grille peuplee, on laisse encore deux frames au layout pour
	 * que les skeletons des <nosto-dynamic-card> calculent leur hauteur, puis on
	 * swappe vers une grille deja a sa hauteur finale (le skeleton de section a
	 * tenu l'espace jusqu'ici).
	 */
	_scheduleReady() {
		if (this._readyScheduled) return;
		this._readyScheduled = true;
		if (this._observer) {
			this._observer.disconnect();
			this._observer = null;
		}
		requestAnimationFrame(() => requestAnimationFrame(() => this._ready()));
	}

	disconnectedCallback() {
		this._cleanup();
	}

	_ready() {
		// Cleanup timers/observers and flip state. Crucially, if the
		// fallback class was already applied (Nosto took longer than the
		// timeout, e.g. search page or slow network), we now swap it for
		// `nosto-loaded` so the React tree replaces the native fallback.
		this._cleanup();
		this.classList.remove('nosto-fallback');
		this.classList.add('nosto-loaded');
		this.dispatchEvent(new CustomEvent('nosto:ready', { bubbles: true }));
	}

	_fallback() {
		// Only the timer is cleared; the observer keeps running so a late
		// Nosto mount can still promote us to `loaded`.
		if (this._timeoutId) {
			clearTimeout(this._timeoutId);
			this._timeoutId = null;
		}
		// Avoid flipping if `nosto-loaded` already won the race.
		if (this.classList.contains('nosto-loaded')) return;
		this.classList.add('nosto-fallback');
		this.dispatchEvent(new CustomEvent('nosto:fallback', { bubbles: true }));
	}

	_cleanup() {
		if (this._observer) {
			this._observer.disconnect();
			this._observer = null;
		}
		if (this._timeoutId) {
			clearTimeout(this._timeoutId);
			this._timeoutId = null;
		}
	}
}

if (!customElements.get('nosto-react')) {
	customElements.define('nosto-react', NostoReact);
}
